import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import { useFetcher } from '@remix-run/react';
import { CommentBox } from './CommentBox';
import { CommentViewer } from './CommentViewer';

import type { loader } from '~/routes/remix-api.app.build-hub-item-comments.$id';
import { LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE } from '~/routes/remix-api.app.build-hub-item-comments.$id';
import type {
    CommentSubmitPayload,
    Comment,
} from '@modules/build-hub-comment/types';

import { AnimatePresence } from 'framer-motion';
import { useUser } from '@modules/user';
import { LoaderCircle } from 'lucide-react';
import { useBuildHubCommentUpdates } from '@modules/build-hub/context/BuildHubRealTimeContext';
import { useCommentMarkAsRead } from '@modules/build-hub-comment/hooks/use-comment-mark-as-read';

interface BuildHubCommentsProps {
    itemId: number;
    unreadCount?: number;
    scrollToCommentId?: number;
}

export const Comments = ({
    itemId,
    unreadCount = 0,
    scrollToCommentId,
}: BuildHubCommentsProps) => {
    const [optimisticComments, setOptimisticComments] = useState<Comment[]>([]);
    const commentsFetcher = useFetcher<typeof loader>();

    const user = useUser();
    const me = user.__typename === 'Me' ? user : undefined;

    const scrollRef = useRef<HTMLDivElement>(null);
    const { markAsRead } = useCommentMarkAsRead();
    const commentRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    // Capture the initial unread count when drawer opens
    // This prevents the separator from jumping when new messages arrive
    const initialUnreadCountRef = useRef(unreadCount);
    const hasInitializedRef = useRef(false);

    // Set initial unread count only once when component first mounts
    if (!hasInitializedRef.current && unreadCount > 0) {
        initialUnreadCountRef.current = unreadCount;
        hasInitializedRef.current = true;
    }

    const isLoading =
        commentsFetcher.state === 'loading' && !commentsFetcher.data;

    // Initial fetch
    useEffect(() => {
        if (!itemId) return;

        const url = `${LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE}/${itemId}`;
        commentsFetcher.load(url);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId]);

    // Mark as read when comments are rendered (initial load)
    useEffect(() => {
        if (!itemId || isLoading) return;

        // Mark as read after comments have loaded
        // Use a small delay to ensure comments are visible
        const timeoutId = setTimeout(() => {
            markAsRead(String(itemId));
            // Note: We don't reset initialUnreadCountRef here
            // The separator stays visible until the drawer closes
        }, 500);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId, isLoading]);

    // Reset initial unread count when component unmounts (drawer closes)
    useEffect(() => {
        return () => {
            initialUnreadCountRef.current = 0;
            hasInitializedRef.current = false;
        };
    }, []);

    // Subscribe to BuildHub real-time updates for comments
    useBuildHubCommentUpdates(itemId, () => {
        // Refetch comments to get the new comment
        // The existing logic will handle removing optimistic comments
        // that match server comments
        const url = `${LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE}/${itemId}`;
        commentsFetcher.load(url);
    });

    const handlePosted = (payload: CommentSubmitPayload) => {
        // Create optimistic comment using fetcher.formData
        const optimisticComment: Comment = {
            id: Date.now(), // Use timestamp as temporary ID
            message: payload.message,
            createdAt: new Date().toISOString(),
            editedAt: new Date().toISOString(),
            link: '',
            author: {
                id: user.ID,
                name: me?.Name ?? '',
                email: me?.Email ?? '',
                avatar: me?.ProfileImageLink ?? '',
            },
            mentions: payload.entities
                .filter((e) => e.type === 'user' || e.type === 'owner')
                .map((e) => ({
                    id: Number(e.id),
                    name: e.label,
                    email: '',
                })),
            files: payload.entities
                .filter((e) => e.type === 'file')
                .map((e) => ({
                    id: Number(e.id),
                    title: e.label,
                })),
        };

        setOptimisticComments((prev) => [...prev, optimisticComment]);
    };

    const allComments = useMemo(() => {
        const serverComments: Comment[] = commentsFetcher.data?.comments ?? [];

        return [
            ...serverComments,
            ...optimisticComments.filter((optComment) => {
                // Check if there's a server comment from the same user with the same message
                const hasMatchingServerComment = serverComments.some(
                    (serverComment) =>
                        serverComment.author.id === optComment.author.id &&
                        serverComment.message === optComment.message
                );

                // Keep optimistic comment if no matching server comment found
                return !hasMatchingServerComment;
            }),
        ];
    }, [commentsFetcher.data?.comments, optimisticComments]);

    // Calculate which comments are unread based on position
    // Use the initial unread count (frozen when drawer opened) for the separator
    // This prevents the separator from jumping when new messages arrive while viewing
    const commentsFromOthers = allComments.filter(
        (c) => c.author.id !== user.ID
    );
    const unreadStartIndexInOthersComments = Math.max(
        0,
        commentsFromOthers.length - initialUnreadCountRef.current
    );

    // Create a Set of unread comment IDs for fast lookup
    const unreadCommentIds = new Set(
        commentsFromOthers
            .slice(unreadStartIndexInOthersComments)
            .map((c) => c.id)
    );

    // Callback to handle marking a specific comment as unread
    const handleMarkCommentAsUnread = useCallback(
        (commentId: number) => {
            // Find the comment in all comments
            const commentIndex = allComments.findIndex(
                (c) => c.id === commentId
            );
            if (commentIndex === -1) return;

            // Find how many comments from others exist from this point onwards
            const commentsFromThisPointOnwards = allComments
                .slice(commentIndex)
                .filter((c) => c.author.id !== user.ID);

            // Update the initial unread count for immediate visual feedback
            initialUnreadCountRef.current = commentsFromThisPointOnwards.length;
        },
        [allComments, user.ID]
    );

    // Scroll to specific comment when scrollToCommentId is provided
    useEffect(() => {
        if (
            scrollToCommentId &&
            commentRefs.current.has(scrollToCommentId) &&
            !isLoading
        ) {
            // Small delay to ensure the drawer animation is complete
            setTimeout(() => {
                const element = commentRefs.current.get(scrollToCommentId);
                element?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 300);
        }
    }, [scrollToCommentId, allComments.length, isLoading]);

    // scroll to bottom when comments change (but not when scrollToCommentId is provided)
    useEffect(() => {
        if (scrollRef.current && !scrollToCommentId) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [allComments.length, scrollToCommentId]);

    // Callback to set ref for a comment
    const setCommentRef = useCallback(
        (id: number, el: HTMLDivElement | null) => {
            if (el) {
                commentRefs.current.set(id, el);
            } else {
                commentRefs.current.delete(id);
            }
        },
        []
    );

    return (
        <div className="flex h-full flex-col">
            <div className="min-h-0 flex-1">
                <div
                    ref={scrollRef}
                    className="h-full space-y-2 overflow-y-auto"
                >
                    {isLoading ? (
                        <div className="flex h-full items-center justify-center pb-4">
                            <LoaderCircle className="size-4 animate-spin" />
                        </div>
                    ) : (
                        <AnimatePresence initial={false}>
                            {allComments.length === 0 && (
                                <div className="flex h-full items-center justify-center pb-4">
                                    <p className="text-sm text-gray-400">
                                        {`Be the first to leave a comment`}
                                    </p>
                                </div>
                            )}
                            {allComments.map((c, index) => {
                                const isUnread = unreadCommentIds.has(c.id);

                                // Check if this is the first unread comment
                                const prevComment =
                                    index > 0 ? allComments[index - 1] : null;
                                const isFirstUnread =
                                    isUnread &&
                                    (index === 0 ||
                                        !prevComment ||
                                        !unreadCommentIds.has(prevComment.id));

                                return (
                                    <React.Fragment key={c.id}>
                                        {isFirstUnread && (
                                            <div className="relative my-4">
                                                <div className="absolute inset-0 flex items-center">
                                                    <div className="w-full border-t border-red-300" />
                                                </div>
                                                <div className="relative flex justify-center">
                                                    <span className="bg-white px-3 text-xs font-medium text-red-500">
                                                        New messages
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <CommentViewer
                                            comment={c}
                                            documentId={String(itemId)}
                                            onMarkAsUnread={
                                                handleMarkCommentAsUnread
                                            }
                                            isHighlighted={
                                                scrollToCommentId === c.id
                                            }
                                            setRef={setCommentRef}
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </AnimatePresence>
                    )}
                </div>
            </div>
            <div className="flex-shrink-0 border-t border-gray-200 pb-4 pt-4">
                <CommentBox
                    documentId={String(itemId)}
                    onPosted={handlePosted}
                />
            </div>
        </div>
    );
};
